
/**
 * Module dependencies.
 */

import { useForm } from 'react-hook-form';
import { useTranslate } from 'src/core/utils/translator';
import Input from 'src/components/core/forms/fields/input';
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import Sidebar from 'src/components/my-revigres/core/sidebar';
import Upload from 'src/components/core/forms/fields/upload';
import useAutoFocus from 'src/hooks/use-auto-focus';

/**
 * `Props` type.
 */

type Props = {
  className?: string,
  isOpen: boolean,
  isSubmitting: boolean,
  onClose: () => void,
  onSubmit: (data: FormData) => void
};

/**
 * `CreateProjectSidebar` component.
 */

const CreateProjectSidebar = (props: Props): ReactElement => {
  const { isOpen, isSubmitting, onClose, onSubmit } = props;
  const { translate } = useTranslate();
  const inputRef = useRef();

  const {
    clearErrors,
    control,
    errors,
    handleSubmit,
    register,
    reset,
    setError,
    setValue
  } = useForm({ mode: 'onBlur' });

  const handleClearValue = useCallback((name: string) => {
    setValue(name, null);
  }, [setValue]);

  const [isSubmitted, setIsSubmitted] = useState<boolean>();
  const onSubmitForm = useCallback(data => {
    const { description, image, title } = data;
    const formData = new FormData();

    formData.append('title', title);

    if (description) {
      formData.append('description', description);
    }

    if (image) {
      formData.append('image', image);
    }

    setIsSubmitted(true);
    onSubmit(formData);
  }, [onSubmit]);

  useAutoFocus({
    isActive: isOpen,
    ref: inputRef
  });

  useEffect(() => {
    if (!isOpen) {
      handleClearValue('file');
      reset();
    }
  }, [handleClearValue, isOpen, reset]);

  return (
    <Sidebar
      isOpen={isOpen}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmitForm)}
      title={translate('my-revigres:projects.createProject')}
    >
      <Input
        disabled={isSubmitting}
        error={errors?.title}
        label={translate('forms:titleLabel')}
        name={'title'}
        placeholder={translate('forms:projectTitlePlaceholder')}
        ref={element => {
          // @ts-ignore
          inputRef.current = element;
          register<any>(element, {
            required: {
              message: translate('forms:requiredField'),
              value: true
            }
          });
        }}
      />

      <Upload
        accept={'image/*'}
        clearError={clearErrors}
        clearValue={handleClearValue}
        control={control}
        disabled={isSubmitting}
        error={errors?.image}
        helpText={'Max. 2mb'}
        // Force re-render to clear value.
        // @ts-ignore
        key={isSubmitted}
        label={translate('forms:uploadImage')}
        maxSize={2000000}
        name={'image'}
        rules={register}
        setError={setError}
        setValue={setValue}
      />

      <Input
        as={'textarea'}
        disabled={isSubmitting}
        error={errors?.description}
        label={translate('forms:descriptionLabel')}
        name={'description'}
        placeholder={translate('forms:descriptionPlaceholder')}
        ref={register}
        rows={6}
      />
    </Sidebar>
  );
};

/**
 * Export `CreateProjectSidebar` component.
 */

export default CreateProjectSidebar;
